import React, { useState, memo, useMemo, useEffect } from 'react';
import { Modal, Box, Button, TablePagination, TextField, Collapse, InputAdornment, FormControlLabel, Checkbox, Chip, TableSortLabel } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTranslation } from 'react-i18next';
import PCNDocumentationModal from './PCNDocumentationModal';
import { calculateVatAmount, roundVatToInteger } from "../../Utils/GetVatValue";
import { beautifyNumber } from "../../Utils/FormatNumber";
import { PCNCodeTranslationKeys } from "../../Utils/PCN874Constants";
import { computeUniformVatGroupIds, collapseGroupedSameVatDocs } from "../../Utils/pcnGrouping";

const getBeforeVatAmount = (doc) => {
    const vat = calculateVatAmount(doc.amount, doc.vat, doc.documentDate);
    return doc.amount - vat;
};

/** For income: use backend DisplayVatAmount/DisplayAmountBeforeVat when present (code-1 JEs), else compute.
 *  Display values from the backend are always absolute; apply the sign from doc.amount when negative. */
const getIncomeDisplayVatAndNet = (doc) => {
    const rawVat = doc.displayVatAmount ?? doc.DisplayVatAmount;
    const rawNet = doc.displayAmountBeforeVat ?? doc.DisplayAmountBeforeVat;
    if (rawVat != null && rawNet != null) {
        const sign = doc.amount < 0 ? -1 : 1;
        const vatAmount = sign * rawVat;
        const netAmount = sign * rawNet;
        return { vatAmount, netAmount, totalAmount: netAmount + vatAmount };
    }
    const vat = calculateVatAmount(doc.amount, doc.vat, doc.documentDate);
    const net = doc.amount - vat;
    return { vatAmount: vat, netAmount: net, totalAmount: doc.amount };
};

/** For expenses/equipment: use backend DisplayVatAmount/DisplayAmountBeforeVat when present (e.g. Import R), else compute.
 *  Display values from the backend are always absolute; apply the sign from doc.amount when negative. */
const getExpenseDisplayVatAndNet = (doc) => {
    const rawVat = doc.displayVatAmount ?? doc.DisplayVatAmount;
    const rawNet = doc.displayAmountBeforeVat ?? doc.DisplayAmountBeforeVat;
    if (rawVat != null && rawNet != null) {
        const sign = doc.amount < 0 ? -1 : 1;
        const vatAmount = sign * rawVat;
        const netAmount = sign * rawNet;
        return { vatAmount, netAmount, totalAmount: netAmount + vatAmount };
    }
    const vat = calculateVatAmount(doc.amount, doc.vat, doc.documentDate);
    const net = doc.amount - vat;
    return { vatAmount: vat, netAmount: net, totalAmount: doc.amount };
};

/** Returns a color for an amount cell: red for negative, the provided default for positive/zero. */
const amountColor = (value, defaultColor) => value < 0 ? '#d32f2f' : defaultColor;

// Helper functions for document type display
const getDocumentTypeColor = (documentType) => {
    switch (documentType) {
        case 'VATTransactions':
            return '#28a745';
        case 'VATExpenses':
            return '#dc3545';
        case 'EquipmentVAT':
            return '#007bff';
        default:
            return '#6c757d';
    }
};

/** Get display type for a doc (M, C, R, etc.) for incomes section. */
const getIncomeDocType = (doc, pcnReportDetails) =>
    doc.pcnCompositeType === 'C+M' ? 'M' : (pcnReportDetails?.incomeType?.[doc.id] ?? pcnReportDetails?.incomeType?.[doc.Id] ?? '-');

/** Get display type for a doc (M, C, R, etc.) for expenses/equipment sections. */
const getExpenseDocType = (doc, pcnReportDetails) =>
    doc.pcnCompositeType === 'C+M' ? 'C' : (pcnReportDetails?.expenseType?.[doc.id] ?? pcnReportDetails?.expenseType?.[doc.Id] ?? '-');

/** Returns true if doc matches searchQuery (reference, entry, amounts, date, companyId, description). */
const docMatchesSearch = (doc, searchQuery, getDisplayVatAndNet) => {
    if (!searchQuery || !searchQuery.trim()) return true;
    const q = searchQuery.trim().toLowerCase();
    const { netAmount, vatAmount, totalAmount } = getDisplayVatAndNet(doc);
    const dateStr = doc.documentDate ? new Date(doc.documentDate).toLocaleDateString('he-IL') : '';
    const entryStr = `${doc.uploadNumber || ''} ${dateStr}`;
    const ref = (doc.reference || '').toLowerCase();
    const accountCaseId = (doc.accountCaseId || '').toString().toLowerCase();
    const desc = (doc.description || '').toLowerCase();
    const amounts = [netAmount, vatAmount, totalAmount].map((n) => n?.toString() || '').join(' ');
    return (
        ref.includes(q) ||
        entryStr.toLowerCase().includes(q) ||
        amounts.includes(q) ||
        accountCaseId.includes(q) ||
        desc.includes(q)
    );
};

const PCNReportDetailsModal = ({ 
    open, 
    onClose, 
    pcnReportDetails, 
    onDocumentClick, 
    direction = 'rtl' 
}) => {
    const { t } = useTranslation();
    const [showDocModal, setShowDocModal] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTypes, setSelectedTypes] = useState({}); // { M: true, C: false, ... }
    const [filtersOpen, setFiltersOpen] = useState(false);
    // Pagination states for each table
    const [incomesPage, setIncomesPage] = useState(0);
    const [incomesRowsPerPage, setIncomesRowsPerPage] = useState(10);
    const [expensesPage, setExpensesPage] = useState(0);
    const [expensesRowsPerPage, setExpensesRowsPerPage] = useState(10);
    const [equipmentPage, setEquipmentPage] = useState(0);
    const [equipmentRowsPerPage, setEquipmentRowsPerPage] = useState(10);

    // Group ids whose rows are expanded to reveal the individual grouped documents.
    const [expandedGroups, setExpandedGroups] = useState(() => new Set());
    const toggleGroup = (gid) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(gid)) next.delete(gid); else next.add(gid);
            return next;
        });
    };

    // Groups whose every document shares one VAT rate. These collapse into a single
    // row (mirroring what the backend reports to the tax authority); mixed-VAT groups
    // are left as individual rows.
    const uniformVatGroupIds = useMemo(
        () => computeUniformVatGroupIds(pcnReportDetails),
        [pcnReportDetails]
    );

    const collapsedIncomes = useMemo(
        () => collapseGroupedSameVatDocs(pcnReportDetails?.incomes, uniformVatGroupIds),
        [pcnReportDetails, uniformVatGroupIds]
    );
    const collapsedExpenses = useMemo(
        () => collapseGroupedSameVatDocs(pcnReportDetails?.expenses, uniformVatGroupIds),
        [pcnReportDetails, uniformVatGroupIds]
    );
    const collapsedEquipment = useMemo(
        () => collapseGroupedSameVatDocs(pcnReportDetails?.equipmentTransactions, uniformVatGroupIds),
        [pcnReportDetails, uniformVatGroupIds]
    );

    // Extract unique sub-types (M, C, R, etc.) from data
    const availableTypes = useMemo(() => {
        if (!pcnReportDetails) return [];
        const types = new Set();
        collapsedIncomes.forEach((doc) => types.add(getIncomeDocType(doc, pcnReportDetails)));
        collapsedExpenses.forEach((doc) => types.add(getExpenseDocType(doc, pcnReportDetails)));
        collapsedEquipment.forEach((doc) => types.add(getExpenseDocType(doc, pcnReportDetails)));
        return Array.from(types).filter((x) => x && x !== '-').sort();
    }, [pcnReportDetails, collapsedIncomes, collapsedExpenses, collapsedEquipment]);

    // Filter documents by search and selected types
    const filterDocs = (docs, isIncome) => {
        const getType = isIncome ? (d) => getIncomeDocType(d, pcnReportDetails) : (d) => getExpenseDocType(d, pcnReportDetails);
        const getDisplayVatAndNet = isIncome ? getIncomeDisplayVatAndNet : getExpenseDisplayVatAndNet;
        const selectedTypesArr = Object.entries(selectedTypes).filter(([, v]) => v).map(([k]) => k);
        const typeFilterActive = selectedTypesArr.length > 0;

        return (docs || []).filter((doc) => {
            if (!docMatchesSearch(doc, searchQuery, getDisplayVatAndNet)) return false;
            if (typeFilterActive) {
                const docType = getType(doc);
                if (!selectedTypesArr.includes(docType)) return false;
            }
            return true;
        });
    };

    const filteredIncomes = useMemo(
        () => (pcnReportDetails ? filterDocs(collapsedIncomes, true) : []),
        [pcnReportDetails, collapsedIncomes, searchQuery, selectedTypes]
    );
    const filteredExpenses = useMemo(
        () => (pcnReportDetails ? filterDocs(collapsedExpenses, false) : []),
        [pcnReportDetails, collapsedExpenses, searchQuery, selectedTypes]
    );
    const filteredEquipment = useMemo(
        () => (pcnReportDetails ? filterDocs(collapsedEquipment, false) : []),
        [pcnReportDetails, collapsedEquipment, searchQuery, selectedTypes]
    );

    // Per-type stats for cards: { typeCode: { count, beforeVat, vat, total, descKey } }
    const typeStats = useMemo(() => {
        const stats = {};
        const addDoc = (doc, getType, getDisplay) => {
            const type = getType(doc);
            if (!type || type === '-') return;
            if (!stats[type]) stats[type] = { count: 0, beforeVat: 0, vat: 0, total: 0 };
            const { netAmount, vatAmount, totalAmount } = getDisplay(doc);
            stats[type].count += 1;
            stats[type].beforeVat += netAmount;
            stats[type].vat += vatAmount;
            stats[type].total += totalAmount;
        };
        collapsedIncomes.forEach(doc => addDoc(doc, (d) => getIncomeDocType(d, pcnReportDetails), getIncomeDisplayVatAndNet));
        collapsedExpenses.forEach(doc => addDoc(doc, (d) => getExpenseDocType(d, pcnReportDetails), getExpenseDisplayVatAndNet));
        collapsedEquipment.forEach(doc => addDoc(doc, (d) => getExpenseDocType(d, pcnReportDetails), getExpenseDisplayVatAndNet));
        Object.keys(stats).forEach(t => { stats[t].descKey = PCNCodeTranslationKeys[t] || t; });
        return stats;
    }, [pcnReportDetails, collapsedIncomes, collapsedExpenses, collapsedEquipment]);

    // Sort state: { orderBy, order } for each table
    const [incomesSort, setIncomesSort] = useState({ orderBy: 'documentDate', order: 'desc' });
    const [expensesSort, setExpensesSort] = useState({ orderBy: 'documentDate', order: 'desc' });
    const [equipmentSort, setEquipmentSort] = useState({ orderBy: 'documentDate', order: 'desc' });

    const createSortHandler = (table, field) => () => {
        const setter = table === 'incomes' ? setIncomesSort : table === 'expenses' ? setExpensesSort : setEquipmentSort;
        const state = table === 'incomes' ? incomesSort : table === 'expenses' ? expensesSort : equipmentSort;
        setter({ orderBy: field, order: state.orderBy === field && state.order === 'asc' ? 'desc' : 'asc' });
    };

    const sortDocs = (docs, orderBy, order, getDisplay, getType) => {
        const cmp = (a, b) => {
            let va, vb;
            switch (orderBy) {
                case 'entry': va = `${a.uploadNumber || ''}_${a.documentDate}`; vb = `${b.uploadNumber || ''}_${b.documentDate}`; return (va || '').localeCompare(vb || '');
                case 'type': va = getType(a); vb = getType(b); return (va || '').localeCompare(vb || '');
                case 'reference': va = a.reference || ''; vb = b.reference || ''; return va.localeCompare(vb);
                case 'documentDate': va = new Date(a.documentDate).getTime(); vb = new Date(b.documentDate).getTime(); return va - vb;
                case 'amountBeforeVat': return getDisplay(a).netAmount - getDisplay(b).netAmount;
                case 'vatAmount': return getDisplay(a).vatAmount - getDisplay(b).vatAmount;
                case 'totalAmount': return getDisplay(a).totalAmount - getDisplay(b).totalAmount;
                case 'companyId': va = (a.accountCaseId || '').toString(); vb = (b.accountCaseId || '').toString(); return va.localeCompare(vb);
                case 'vatPercentage': return (a.vat || 0) - (b.vat || 0);
                case 'description': va = (a.description || ''); vb = (b.description || ''); return va.localeCompare(vb);
                default: return 0;
            }
        };
        const sorted = [...docs].sort((a, b) => {
            const r = cmp(a, b);
            return order === 'asc' ? r : -r;
        });
        return sorted;
    };

    const sortedIncomes = useMemo(() =>
        sortDocs(filteredIncomes, incomesSort.orderBy, incomesSort.order, getIncomeDisplayVatAndNet, (d) => getIncomeDocType(d, pcnReportDetails)),
        [filteredIncomes, incomesSort]);
    const sortedExpenses = useMemo(() =>
        sortDocs(filteredExpenses, expensesSort.orderBy, expensesSort.order, getExpenseDisplayVatAndNet, (d) => getExpenseDocType(d, pcnReportDetails)),
        [filteredExpenses, expensesSort]);
    const sortedEquipment = useMemo(() =>
        sortDocs(filteredEquipment, equipmentSort.orderBy, equipmentSort.order, getExpenseDisplayVatAndNet, (d) => getExpenseDocType(d, pcnReportDetails)),
        [filteredEquipment, equipmentSort]);

    // Reset pagination when filters change
    useEffect(() => {
        setIncomesPage(0);
        setExpensesPage(0);
        setEquipmentPage(0);
    }, [searchQuery, selectedTypes, selectedFilter]);

    if (!pcnReportDetails) {
        return null;
    }

    const activeFiltersCount = (searchQuery?.trim() ? 1 : 0) + Object.values(selectedTypes).filter(Boolean).length;

    const handleTypeToggle = (type) => {
        setSelectedTypes((prev) => ({ ...prev, [type]: !prev[type] }));
    };
    const clearFilters = () => {
        setSearchQuery('');
        setSelectedTypes({});
    };

    // Check if we have data for each type
    const hasIncomes = pcnReportDetails.incomes && pcnReportDetails.incomes.length > 0;
    const hasExpenses = pcnReportDetails.expenses && pcnReportDetails.expenses.length > 0;
    const hasEquipmentTransactions = pcnReportDetails.equipmentTransactions && pcnReportDetails.equipmentTransactions.length > 0;

    // Backend pre-computed totals (PCN-rounded per document)
    const getBackendTotals = () => {
        if (pcnReportDetails.incomeVatSum == null && pcnReportDetails.expenseVatSum == null && pcnReportDetails.equipmentInputVat == null)
            return null;
        const incVat = pcnReportDetails.incomeVatSum ?? 0;
        const incSum = pcnReportDetails.incomeSum ?? 0;
        const expVat = pcnReportDetails.expenseVatSum ?? 0;
        const expSum = pcnReportDetails.expenseSum ?? 0;
        const eqVat  = pcnReportDetails.equipmentInputVat ?? 0;
        const eqSum  = pcnReportDetails.equipmentInputSum ?? 0;
        return {
            incomes:   { beforeVat: incSum - incVat, vat: incVat, total: incSum },
            expenses:  { beforeVat: expSum - expVat, vat: expVat, total: expSum },
            equipment: { beforeVat: eqSum - eqVat,  vat: eqVat,  total: eqSum  },
        };
    };

    // Client-side sum from individual documents (unrounded)
    const getClientTotals = () => {
        const incomes = pcnReportDetails.incomes || [];
        const expenses = pcnReportDetails.expenses || [];
        const equipment = pcnReportDetails.equipmentTransactions || [];

        const incomesBeforeVat = incomes.reduce((sum, doc) => sum + getIncomeDisplayVatAndNet(doc).netAmount, 0);
        const incomesVat = incomes.reduce((sum, doc) => sum + getIncomeDisplayVatAndNet(doc).vatAmount, 0);
        const incomesTotal = incomes.reduce((sum, doc) => sum + getIncomeDisplayVatAndNet(doc).totalAmount, 0);

        const expensesBeforeVat = expenses.reduce((sum, doc) => sum + getExpenseDisplayVatAndNet(doc).netAmount, 0);
        const expensesVat = expenses.reduce((sum, doc) => sum + getExpenseDisplayVatAndNet(doc).vatAmount, 0);
        const expensesTotal = expenses.reduce((sum, doc) => sum + getExpenseDisplayVatAndNet(doc).totalAmount, 0);

        const equipmentBeforeVat = equipment.reduce((sum, doc) => sum + getExpenseDisplayVatAndNet(doc).netAmount, 0);
        const equipmentVat = equipment.reduce((sum, doc) => sum + getExpenseDisplayVatAndNet(doc).vatAmount, 0);
        const equipmentTotal = equipment.reduce((sum, doc) => sum + getExpenseDisplayVatAndNet(doc).totalAmount, 0);

        return {
            incomes: { beforeVat: incomesBeforeVat, vat: incomesVat, total: incomesTotal },
            expenses: { beforeVat: expensesBeforeVat, vat: expensesVat, total: expensesTotal },
            equipment: { beforeVat: equipmentBeforeVat, vat: equipmentVat, total: equipmentTotal }
        };
    };

    const backendTotals = getBackendTotals();
    const clientTotals = getClientTotals();
    const summaryTotals = backendTotals || clientTotals;

    // Calculate net VAT (to pay or refund)
    const netVat = summaryTotals.incomes.vat - (summaryTotals.expenses.vat + summaryTotals.equipment.vat);
    const isRefund = netVat < 0;

    // Handle row click
    const handleRowClick = (item) => {
        
        
        if (onDocumentClick) {  
            onDocumentClick({
                id: item.id,
                documentType: item.docType
            });
        }
    };

    // Render the individual documents of an expanded grouped row as indented child rows.
    const renderGroupChildren = (parent, getDisplay, getType, sectionKey) => {
        if (!parent._grouped || parent._groupedCount <= 1) return null;
        const expandKey = parent._mergeKey || parent._groupId;
        if (!expandedGroups.has(expandKey)) return null;
        return (parent._children || []).map((child, ci) => {
            const { vatAmount, netAmount, totalAmount: rowTotal } = getDisplay(child);
            return (
                <tr
                    key={`${sectionKey}-child-${parent._groupId}-${child.id ?? child.Id ?? ci}`}
                    onClick={() => handleRowClick(child)}
                    style={{ cursor: 'pointer', backgroundColor: '#faf5ff', borderBottom: '1px dashed #e1d5f5', fontSize: '13px' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3e9ff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#faf5ff'; }}
                >
                    <td style={{ padding: '8px', textAlign: 'center', color: '#5e35b1' }}>
                        ↳ {`(${child.uploadNumber || 'N/A'}) ${new Date(child.documentDate).toLocaleDateString('he-IL', { month: '2-digit', year: '2-digit' })}`}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>{getType(child)}</td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>{child.reference || '-'}</td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>{new Date(child.documentDate).toLocaleDateString('he-IL')}</td>
                    <td style={{ padding: '8px', textAlign: 'center', color: amountColor(netAmount, '#dc3545') }}>{beautifyNumber(netAmount)}</td>
                    <td style={{ padding: '8px', textAlign: 'center', color: amountColor(vatAmount, '#28a745') }}>{beautifyNumber(vatAmount)}</td>
                    <td style={{ padding: '8px', textAlign: 'center', color: amountColor(rowTotal, 'inherit') }}>{beautifyNumber(rowTotal)}</td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>{child.accountCaseId || '-'}</td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>{child.vat}%</td>
                    <td style={{ padding: '8px', textAlign: 'center', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={child.description || ''}>{child.description || '-'}</td>
                </tr>
            );
        });
    };

    return (
        <>
        <PCNDocumentationModal open={showDocModal} onClose={() => setShowDocModal(false)} />
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="pcn-report-details-modal-title"
            aria-describedby="pcn-report-details-modal-description"
            sx={{ zIndex: 1300 }}
            disableEnforceFocus disableAutoFocus
        >
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '90%',
                maxWidth: 1200,
                maxHeight: '90vh',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: 24,
                padding: 3,
                outline: 'none',
                overflow: 'auto'
            }} className={`modal-container ${direction}`}>
                
                {/* Header */}
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    marginBottom: '20px',
                    borderBottom: '1px solid #dee2e6',
                    paddingBottom: '15px'
                }}>
                    <h2 style={{ 
                        margin: 0,
                        fontSize: '24px',
                        fontWeight: 600,
                        color: 'rgba(0, 0, 0, 0.90)'
                    }}>
                        {t('PCNReportDetails', { clientName: pcnReportDetails.clientName || 'Unknown' })}
                    </h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Button
                            size="small"
                            startIcon={<HelpOutlineIcon />}
                            onClick={() => setShowDocModal(true)}
                            sx={{ textTransform: 'none' }}
                        >
                            {t('ViewPcnDocumentation')}
                        </Button>
                        <Button 
                            onClick={onClose}
                        sx={{ 
                            minWidth: 'auto',
                            padding: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#f8f9fa',
                            '&:hover': {
                                backgroundColor: '#e9ecef'
                            }
                        }}
                    >
                        ✕
                    </Button>
                    </div>
                </div>

                {/* Summary Section */}
                <div style={{
                    marginBottom: '20px',
                    padding: '12px 16px',
                    backgroundColor: '#fafbfc',
                    borderRadius: '10px',
                    border: '1px solid #e8eaed',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                }}>
                    <h3 style={{
                        margin: '0 0 12px 0',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#5f6368',
                        letterSpacing: '0.3px'
                    }}>
                        {t('Summary')}
                    </h3>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: '10px'
                    }}>
                        {hasIncomes && (
                            <div style={{
                                padding: '8px 12px',
                                backgroundColor: '#e8f5e9',
                                borderRadius: '8px',
                                border: '1px solid #c8e6c9',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                            }}>
                                <div style={{ fontSize: '11px', fontWeight: 600, color: '#2e7d32', marginBottom: 6 }}>{t("VATTransactions")}</div>
                                <div style={{ fontSize: '11px', lineHeight: 1.5, color: '#37474f' }}>
                                    <div>{t('AmountBeforeVAT')}: <strong>{beautifyNumber(summaryTotals.incomes.beforeVat)}</strong></div>
                                    <div>{t('VATAmount')}: <strong>{beautifyNumber(summaryTotals.incomes.vat)}</strong></div>
                                    <div>{t('TotalAmountIncludingVAT')}: <strong>{beautifyNumber(summaryTotals.incomes.total)}</strong></div>
                                </div>
                                {backendTotals && (
                                    <div style={{ fontSize: '10px', lineHeight: 1.4, color: '#9e9e9e', marginTop: 4, borderTop: '1px dashed #c8e6c9', paddingTop: 4 }}>
                                        {t('AmountBeforeVAT')}: {beautifyNumber(clientTotals.incomes.beforeVat)} | {t('VATAmount')}: {beautifyNumber(clientTotals.incomes.vat)} | {t('TotalAmountIncludingVAT')}: {beautifyNumber(clientTotals.incomes.total)}
                                    </div>
                                )}
                            </div>
                        )}
                        {hasExpenses && (
                            <div style={{
                                padding: '8px 12px',
                                backgroundColor: '#ffebee',
                                borderRadius: '8px',
                                border: '1px solid #ffcdd2',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                            }}>
                                <div style={{ fontSize: '11px', fontWeight: 600, color: '#c62828', marginBottom: 6 }}>{t("VATExpenses")}</div>
                                <div style={{ fontSize: '11px', lineHeight: 1.5, color: '#37474f' }}>
                                    <div>{t('AmountBeforeVAT')}: <strong>{beautifyNumber(summaryTotals.expenses.beforeVat)}</strong></div>
                                    <div>{t('VATAmount')}: <strong>{beautifyNumber(summaryTotals.expenses.vat)}</strong></div>
                                    <div>{t('TotalAmountIncludingVAT')}: <strong>{beautifyNumber(summaryTotals.expenses.total)}</strong></div>
                                </div>
                                {backendTotals && (
                                    <div style={{ fontSize: '10px', lineHeight: 1.4, color: '#9e9e9e', marginTop: 4, borderTop: '1px dashed #ffcdd2', paddingTop: 4 }}>
                                        {t('AmountBeforeVAT')}: {beautifyNumber(clientTotals.expenses.beforeVat)} | {t('VATAmount')}: {beautifyNumber(clientTotals.expenses.vat)} | {t('TotalAmountIncludingVAT')}: {beautifyNumber(clientTotals.expenses.total)}
                                    </div>
                                )}
                            </div>
                        )}
                        {hasEquipmentTransactions && (
                            <div style={{
                                padding: '8px 12px',
                                backgroundColor: '#e3f2fd',
                                borderRadius: '8px',
                                border: '1px solid #bbdefb',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                            }}>
                                <div style={{ fontSize: '11px', fontWeight: 600, color: '#1565c0', marginBottom: 6 }}>{t("EquipmentVAT")}</div>
                                <div style={{ fontSize: '11px', lineHeight: 1.5, color: '#37474f' }}>
                                    <div>{t('AmountBeforeVAT')}: <strong>{beautifyNumber(summaryTotals.equipment.beforeVat)}</strong></div>
                                    <div>{t('VATAmount')}: <strong>{beautifyNumber(summaryTotals.equipment.vat)}</strong></div>
                                    <div>{t('TotalAmountIncludingVAT')}: <strong>{beautifyNumber(summaryTotals.equipment.total)}</strong></div>
                                </div>
                                {backendTotals && (
                                    <div style={{ fontSize: '10px', lineHeight: 1.4, color: '#9e9e9e', marginTop: 4, borderTop: '1px dashed #bbdefb', paddingTop: 4 }}>
                                        {t('AmountBeforeVAT')}: {beautifyNumber(clientTotals.equipment.beforeVat)} | {t('VATAmount')}: {beautifyNumber(clientTotals.equipment.vat)} | {t('TotalAmountIncludingVAT')}: {beautifyNumber(clientTotals.equipment.total)}
                                    </div>
                                )}
                            </div>
                        )}
                        <div style={{
                            padding: '8px 12px',
                            backgroundColor: isRefund ? '#fff8e1' : '#e8f5e9',
                            borderRadius: '8px',
                            border: `1px solid ${isRefund ? '#ffe082' : '#a5d6a7'}`,
                            boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                        }}>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: isRefund ? '#f57f17' : '#2e7d32', marginBottom: 4 }}>
                                {isRefund ? t('VATToRefund') : t('VATToPay')}
                            </div>
                            <div style={{ fontSize: '18px', fontWeight: 700, color: isRefund ? '#e65100' : '#1b5e20', marginBottom: 4 }}>
                                {beautifyNumber(Math.abs(netVat))}
                            </div>
                            <div style={{ fontSize: '10px', color: '#616161', lineHeight: 1.4 }}>
                                {t('IncomesVAT')}: {beautifyNumber(summaryTotals.incomes.vat)} · {t('ExpensesVAT')}: {beautifyNumber(summaryTotals.expenses.vat)} · {t('EquipmentVAT')}: {beautifyNumber(summaryTotals.equipment.vat)}
                            </div>
                            {backendTotals && (
                                <div style={{ fontSize: '10px', color: '#9e9e9e', lineHeight: 1.4, marginTop: 4, borderTop: `1px dashed ${isRefund ? '#ffe082' : '#a5d6a7'}`, paddingTop: 4 }}>
                                    {t('IncomesVAT')}: {beautifyNumber(clientTotals.incomes.vat)} · {t('ExpensesVAT')}: {beautifyNumber(clientTotals.expenses.vat)} · {t('EquipmentVAT')}: {beautifyNumber(clientTotals.equipment.vat)}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Type cards - amount and count per PCN type with code meaning */}
                {Object.keys(typeStats).length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: 600, color: '#5f6368' }}>
                            {t('PCNByType', { defaultValue: 'By PCN Type' })}
                        </h4>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {Object.entries(typeStats).sort((a, b) => a[0].localeCompare(b[0])).map(([code, s]) => (
                                <div
                                    key={code}
                                    style={{
                                        padding: '8px 12px',
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: '8px',
                                        border: '1px solid #e0e0e0',
                                        minWidth: '170px',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <span style={{ fontSize: '16px', fontWeight: 700, color: '#424242' }}>{code}</span>
                                        <span style={{ fontSize: '10px', color: '#9e9e9e' }}>{s.count} {t('items', { defaultValue: 'items' })}</span>
                                    </div>
                                    <div style={{ fontSize: '10px', color: '#757575', marginBottom: 6, lineHeight: 1.2 }}>
                                        {t(s.descKey)}
                                    </div>
                                    <div style={{ fontSize: '11px', lineHeight: 1.5, color: '#37474f' }}>
                                        <div>{t('AmountBeforeVAT')}: <strong>{beautifyNumber(s.beforeVat)}</strong></div>
                                        <div>{t('VATAmount')}: <strong>{beautifyNumber(s.vat)}</strong></div>
                                        <div>{t('TotalAmountIncludingVAT')}: <strong>{beautifyNumber(s.total)}</strong></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Search & Filters */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <TextField
                        size="small"
                        placeholder={t('Search')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            ...(direction === 'rtl'
                                ? { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }
                                : { endAdornment: <InputAdornment position="end"><SearchIcon fontSize="small" /></InputAdornment> }
                            ),
                        }}
                        sx={{ minWidth: 200, '& .MuiInputBase-input': { textAlign: direction === 'rtl' ? 'right' : 'left' } }}
                    />
                    <Button
                        size="small"
                        startIcon={<FilterListIcon />}
                        onClick={() => setFiltersOpen((o) => !o)}
                        color={activeFiltersCount > 0 ? 'primary' : 'inherit'}
                        variant={activeFiltersCount > 0 ? 'outlined' : 'text'}
                        sx={{ textTransform: 'none' }}
                    >
                        {t('Filters', { defaultValue: 'Filters' })}
                        {activeFiltersCount > 0 && (
                            <Chip label={activeFiltersCount} size="small" sx={{ ml: 0.5, height: 18 }} />
                        )}
                    </Button>
                    {activeFiltersCount > 0 && (
                        <Button size="small" onClick={clearFilters} sx={{ textTransform: 'none' }}>
                            {t('Clear', { defaultValue: 'Clear' })}
                        </Button>
                    )}
                </Box>
                <Collapse in={filtersOpen} sx={{ width: '100%', mb: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#495057' }}>{t('Type')}:</span>
                        {availableTypes.map((type) => (
                            <FormControlLabel
                                key={type}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={!!selectedTypes[type]}
                                        onChange={() => handleTypeToggle(type)}
                                    />
                                }
                                label={type}
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                            />
                        ))}
                        {availableTypes.length === 0 && (
                            <span style={{ fontSize: 12, color: '#6c757d' }}>{t('NoTypesAvailable', { defaultValue: 'No types available' })}</span>
                        )}
                    </Box>
                </Collapse>

                {/* Section Filter Buttons */}
                <div style={{ 
                    marginBottom: '20px', 
                    display: 'flex', 
                    justifyContent: 'center', 
                    gap: '10px',
                    flexWrap: 'wrap'
                }}>
                    <Button
                        onClick={() => setSelectedFilter('all')}
                        variant={selectedFilter === 'all' ? 'contained' : 'outlined'}
                        sx={{
                            borderRadius: '4px',
                            backgroundColor: selectedFilter === 'all' ? '#007bff' : 'transparent',
                            color: selectedFilter === 'all' ? 'white' : '#495057',
                            borderColor: '#dee2e6',
                            '&:hover': {
                                backgroundColor: selectedFilter === 'all' ? '#0056b3' : '#f8f9fa'
                            }
                        }}
                    >
                        {t('All')}
                    </Button>
                    {hasIncomes && (
                        <Button
                            onClick={() => setSelectedFilter('VATTransactions')}
                            variant={selectedFilter === 'VATTransactions' ? 'contained' : 'outlined'}
                            sx={{
                                borderRadius: '4px',
                                backgroundColor: selectedFilter === 'VATTransactions' ? '#28a745' : 'transparent',
                                color: selectedFilter === 'VATTransactions' ? 'white' : '#495057',
                                borderColor: '#dee2e6',
                                '&:hover': {
                                    backgroundColor: selectedFilter === 'VATTransactions' ? '#1e7e34' : '#f8f9fa'
                                }
                            }}
                        >
                            {t("VATTransactions")}
                        </Button>
                    )}
                    {hasExpenses && (
                        <Button
                            onClick={() => setSelectedFilter('VATExpenses')}
                            variant={selectedFilter === 'VATExpenses' ? 'contained' : 'outlined'}
                            sx={{
                                borderRadius: '4px',
                                backgroundColor: selectedFilter === 'VATExpenses' ? '#dc3545' : 'transparent',
                                color: selectedFilter === 'VATExpenses' ? 'white' : '#495057',
                                borderColor: '#dee2e6',
                                '&:hover': {
                                    backgroundColor: selectedFilter === 'VATExpenses' ? '#c82333' : '#f8f9fa'
                                }
                            }}
                        >
                            {t("VATExpenses")}
                        </Button>
                    )}
                    {hasEquipmentTransactions && (
                        <Button
                            onClick={() => setSelectedFilter('EquipmentVAT')}
                            variant={selectedFilter === 'EquipmentVAT' ? 'contained' : 'outlined'}
                            sx={{
                                borderRadius: '4px',
                                backgroundColor: selectedFilter === 'EquipmentVAT' ? '#007bff' : 'transparent',
                                color: selectedFilter === 'EquipmentVAT' ? 'white' : '#495057',
                                borderColor: '#dee2e6',
                                '&:hover': {
                                    backgroundColor: selectedFilter === 'EquipmentVAT' ? '#0056b3' : '#f8f9fa'
                                }
                            }}
                        >
                            {t("EquipmentVAT")}
                        </Button>
                    )}
                </div>

                {/* Section 1: VATTransactions (Incomes) */}
                {(selectedFilter === 'all' || selectedFilter === 'VATTransactions') && hasIncomes && (
                    <div style={{ marginBottom: '30px' }}>
                        <h3 style={{ 
                            textAlign: 'center', 
                            marginBottom: '15px', 
                            color: '#495057',
                            fontSize: '18px',
                            fontWeight: '600',
                            borderBottom: '2px solid #28a745',
                            paddingBottom: '8px'
                        }}>
                            {t("VATTransactions")}
                        </h3>
                        <div style={{ overflow: "auto" }}>
                            <table style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                fontSize: "14px",
                            }}>
                                <thead style={{ backgroundColor: "#e3f2fd" }}>
                                    <tr style={{ borderBottom: "2px solid rgb(37, 78, 118)" }}>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={incomesSort.orderBy === 'entry'} direction={incomesSort.orderBy === 'entry' ? incomesSort.order : 'asc'} onClick={createSortHandler('incomes', 'entry')}>{t('Entry')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={incomesSort.orderBy === 'type'} direction={incomesSort.orderBy === 'type' ? incomesSort.order : 'asc'} onClick={createSortHandler('incomes', 'type')}>{t('Type')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={incomesSort.orderBy === 'reference'} direction={incomesSort.orderBy === 'reference' ? incomesSort.order : 'asc'} onClick={createSortHandler('incomes', 'reference')}>{t('Reference')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={incomesSort.orderBy === 'documentDate'} direction={incomesSort.orderBy === 'documentDate' ? incomesSort.order : 'asc'} onClick={createSortHandler('incomes', 'documentDate')}>{t('DocumentDate')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={incomesSort.orderBy === 'amountBeforeVat'} direction={incomesSort.orderBy === 'amountBeforeVat' ? incomesSort.order : 'asc'} onClick={createSortHandler('incomes', 'amountBeforeVat')}>{t('AmountBeforeVAT')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={incomesSort.orderBy === 'vatAmount'} direction={incomesSort.orderBy === 'vatAmount' ? incomesSort.order : 'asc'} onClick={createSortHandler('incomes', 'vatAmount')}>{t('VATAmount')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={incomesSort.orderBy === 'totalAmount'} direction={incomesSort.orderBy === 'totalAmount' ? incomesSort.order : 'asc'} onClick={createSortHandler('incomes', 'totalAmount')}>{t('TotalAmountIncludingVAT')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={incomesSort.orderBy === 'companyId'} direction={incomesSort.orderBy === 'companyId' ? incomesSort.order : 'asc'} onClick={createSortHandler('incomes', 'companyId')}>{t('CompanyId')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={incomesSort.orderBy === 'vatPercentage'} direction={incomesSort.orderBy === 'vatPercentage' ? incomesSort.order : 'asc'} onClick={createSortHandler('incomes', 'vatPercentage')}>{t('VATPercentage')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={incomesSort.orderBy === 'description'} direction={incomesSort.orderBy === 'description' ? incomesSort.order : 'asc'} onClick={createSortHandler('incomes', 'description')}>{t('Description')}</TableSortLabel>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedIncomes
                                        .slice(incomesPage * incomesRowsPerPage, incomesPage * incomesRowsPerPage + incomesRowsPerPage)
                                        .flatMap((doc, index) => {
                                        const { vatAmount, netAmount, totalAmount: rowTotal } = getIncomeDisplayVatAndNet(doc);
                                        const totalAmount = sortedIncomes.reduce((sum, d) => sum + getIncomeDisplayVatAndNet(d).totalAmount, 0);
                                        const percentage = totalAmount > 0 ? ((rowTotal / totalAmount) * 100).toFixed(2) : '0.00';
                                        const rowKey = `income-${doc.id ?? doc.Id ?? index}-${index}`;
                                        return [
                                            <tr
                                                key={rowKey}
                                                style={{
                                                    borderBottom: "1px solid #dee2e6",
                                                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8f9fa",
                                                    transition: "background-color 0.2s ease",
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => handleRowClick(doc)}
                                                onMouseEnter={(e) => {
                                                    e.target.closest("tr").style.backgroundColor = "#e3f2fd";
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.closest("tr").style.backgroundColor =
                                                        index % 2 === 0 ? "#ffffff" : "#f8f9fa";
                                                }}
                                            >
                                                <td style={{ padding: "12px 8px", textAlign: "center" }}>
                                                    {`(${doc.uploadNumber || 'N/A'}) ${new Date(doc.documentDate).toLocaleDateString("he-IL", { month: '2-digit', year: '2-digit' })}`}
                                                    {doc._groupedCount > 1 && (
                                                        <Chip
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); toggleGroup(doc._mergeKey || doc._groupId); }}
                                                            label={`${t('Grouped', { defaultValue: 'Grouped' })} (${doc._groupedCount}) ${expandedGroups.has(doc._mergeKey || doc._groupId) ? '▲' : '▼'}`}
                                                            title={(doc._groupedUploads || []).join(', ')}
                                                            sx={{ ml: 0.5, height: 18, fontSize: 10, backgroundColor: '#ede7f6', color: '#5e35b1', cursor: 'pointer', '&:hover': { backgroundColor: '#d1c4e9' } }}
                                                        />
                                                    )}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center" }}>
                                                    {(doc.pcnCompositeType === 'C+M' ? 'M' : (pcnReportDetails.incomeType?.[doc.id] || pcnReportDetails.incomeType?.[doc.Id] || '-'))}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center" }}>
                                                    {doc.reference || '-'}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center" }}>
                                                    {new Date(doc.documentDate).toLocaleDateString("he-IL")}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center",  fontWeight: 600, color: amountColor(netAmount, "#dc3545") }}>
                                                    {beautifyNumber(netAmount)}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, color: amountColor(vatAmount, "#28a745") }}>
                                                    {beautifyNumber(vatAmount)}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, color: amountColor(rowTotal, "inherit") }}>
                                                    {beautifyNumber(rowTotal)}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center" }}>
                                                    {doc.accountCaseId || "-"}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center" }}>
                                                    {doc.vat}%
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={doc.description || ''}>
                                                    {doc.description || '-'}
                                                </td>
                                            </tr>,
                                            ...(renderGroupChildren(doc, getIncomeDisplayVatAndNet, (d) => getIncomeDocType(d, pcnReportDetails), 'income') || []),
                                        ];
                                    })}
                                    {/* Summary Row */}
                                    {(() => {
                                        const totalNet = sortedIncomes.reduce((sum, doc) => sum + getIncomeDisplayVatAndNet(doc).netAmount, 0);
                                        const totalVat = sortedIncomes.reduce((sum, doc) => sum + getIncomeDisplayVatAndNet(doc).vatAmount, 0);
                                        const totalAll = sortedIncomes.reduce((sum, doc) => sum + getIncomeDisplayVatAndNet(doc).totalAmount, 0);
                                        return (
                                    <tr style={{ 
                                        borderTop: "2px solid #dee2e6", 
                                        backgroundColor: "#f8f9fa",
                                        fontWeight: "bold"
                                    }}>
                                        <td colSpan="4" style={{ padding: "12px 8px", textAlign: "center" }}>
                                            {t('Total')}
                                        </td>
                                        <td style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, color: amountColor(totalNet, "#dc3545") }}>
                                            {beautifyNumber(totalNet)}
                                        </td>
                                        <td style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, color: amountColor(totalVat, "#28a745") }}>
                                            {beautifyNumber(totalVat)}
                                        </td>
                                        <td style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, color: amountColor(totalAll, "inherit") }}>
                                            {beautifyNumber(totalAll)}
                                        </td>
                                        <td colSpan="2" style={{ padding: "12px 8px", textAlign: "center" }}>
                                        </td>
                                        <td></td>
                                    </tr>
                                        );
                                    })()}
                                </tbody>
                            </table>
                        </div>
                        {/* pagination */}
                        <TablePagination
                          component="div"
                          count={sortedIncomes.length}
                          page={incomesPage}
                          onPageChange={(_, p) => setIncomesPage(p)}
                          rowsPerPage={incomesRowsPerPage}
                          onRowsPerPageChange={(e) => {
                            setIncomesRowsPerPage(parseInt(e.target.value, 10));
                            setIncomesPage(0);
                          }}
                          rowsPerPageOptions={[10, 25, 50, 100]}
                          labelRowsPerPage={t('Rows per page:')}
                          labelDisplayedRows={({ from, to, count }) =>
                            `${from}–${to} ${t('of')} ${count !== -1 ? count : `> ${to}`}`
                          }
                          sx={{ direction: 'ltr' }}
                        />
                    </div>
                )}

                {/* Section 2: VATExpenses (Expenses) */}
                {(selectedFilter === 'all' || selectedFilter === 'VATExpenses') && hasExpenses && (
                    <div style={{ marginBottom: '30px' }}>
                        <h3 style={{ 
                            textAlign: 'center', 
                            marginBottom: '15px', 
                            color: '#495057',
                            fontSize: '18px',
                            fontWeight: '600',
                            borderBottom: '2px solid #dc3545',
                            paddingBottom: '8px'
                        }}>
                            {t("VATExpenses")}
                        </h3>
                        <div style={{ overflow: "auto" }}>
                            <table style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                fontSize: "14px",
                            }}>
                                <thead style={{ backgroundColor: "#e3f2fd" }}>
                                    <tr style={{ borderBottom: "2px solid rgb(37, 78, 118)" }}>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={expensesSort.orderBy === 'entry'} direction={expensesSort.orderBy === 'entry' ? expensesSort.order : 'asc'} onClick={createSortHandler('expenses', 'entry')}>{t('Entry')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={expensesSort.orderBy === 'type'} direction={expensesSort.orderBy === 'type' ? expensesSort.order : 'asc'} onClick={createSortHandler('expenses', 'type')}>{t('Type')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={expensesSort.orderBy === 'reference'} direction={expensesSort.orderBy === 'reference' ? expensesSort.order : 'asc'} onClick={createSortHandler('expenses', 'reference')}>{t('Reference')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={expensesSort.orderBy === 'documentDate'} direction={expensesSort.orderBy === 'documentDate' ? expensesSort.order : 'asc'} onClick={createSortHandler('expenses', 'documentDate')}>{t('DocumentDate')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={expensesSort.orderBy === 'amountBeforeVat'} direction={expensesSort.orderBy === 'amountBeforeVat' ? expensesSort.order : 'asc'} onClick={createSortHandler('expenses', 'amountBeforeVat')}>{t('AmountBeforeVAT')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={expensesSort.orderBy === 'vatAmount'} direction={expensesSort.orderBy === 'vatAmount' ? expensesSort.order : 'asc'} onClick={createSortHandler('expenses', 'vatAmount')}>{t('VATAmount')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={expensesSort.orderBy === 'totalAmount'} direction={expensesSort.orderBy === 'totalAmount' ? expensesSort.order : 'asc'} onClick={createSortHandler('expenses', 'totalAmount')}>{t('TotalAmountIncludingVAT')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={expensesSort.orderBy === 'companyId'} direction={expensesSort.orderBy === 'companyId' ? expensesSort.order : 'asc'} onClick={createSortHandler('expenses', 'companyId')}>{t('CompanyId')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={expensesSort.orderBy === 'vatPercentage'} direction={expensesSort.orderBy === 'vatPercentage' ? expensesSort.order : 'asc'} onClick={createSortHandler('expenses', 'vatPercentage')}>{t('VATPercentage')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={expensesSort.orderBy === 'description'} direction={expensesSort.orderBy === 'description' ? expensesSort.order : 'asc'} onClick={createSortHandler('expenses', 'description')}>{t('Description')}</TableSortLabel>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedExpenses
                                        .slice(expensesPage * expensesRowsPerPage, expensesPage * expensesRowsPerPage + expensesRowsPerPage)
                                        .flatMap((doc, index) => {
                                        const { vatAmount, netAmount, totalAmount: rowTotal } = getExpenseDisplayVatAndNet(doc);
                                        const totalAmount = sortedExpenses.reduce((sum, d) => sum + getExpenseDisplayVatAndNet(d).totalAmount, 0);
                                        const percentage = totalAmount > 0 ? ((rowTotal / totalAmount) * 100).toFixed(2) : '0.00';
                                        const rowKey = `expense-${doc.id ?? doc.Id ?? index}-${index}`;
                                        return [
                                            <tr
                                                key={rowKey}
                                                style={{
                                                    borderBottom: "1px solid #dee2e6",
                                                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8f9fa",
                                                    transition: "background-color 0.2s ease",
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => handleRowClick(doc)}
                                                onMouseEnter={(e) => {
                                                    e.target.closest("tr").style.backgroundColor = "#e3f2fd";
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.closest("tr").style.backgroundColor =
                                                        index % 2 === 0 ? "#ffffff" : "#f8f9fa";
                                                }}
                                            >
                                                <td style={{ padding: "12px 8px", textAlign: "center" }}>
                                                    {`(${doc.uploadNumber || 'N/A'}) ${new Date(doc.documentDate).toLocaleDateString("he-IL", { month: '2-digit', year: '2-digit' })}`}
                                                    {doc._groupedCount > 1 && (
                                                        <Chip
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); toggleGroup(doc._mergeKey || doc._groupId); }}
                                                            label={`${t('Grouped', { defaultValue: 'Grouped' })} (${doc._groupedCount}) ${expandedGroups.has(doc._mergeKey || doc._groupId) ? '▲' : '▼'}`}
                                                            title={(doc._groupedUploads || []).join(', ')}
                                                            sx={{ ml: 0.5, height: 18, fontSize: 10, backgroundColor: '#ede7f6', color: '#5e35b1', cursor: 'pointer', '&:hover': { backgroundColor: '#d1c4e9' } }}
                                                        />
                                                    )}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center" }}>
                                                    {(doc.pcnCompositeType === 'C+M' ? 'C' : (pcnReportDetails.expenseType?.[doc.id] || pcnReportDetails.expenseType?.[doc.id] || '-'))}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center" }}>
                                                    {doc.reference || '-'}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center" }}>
                                                    {new Date(doc.documentDate).toLocaleDateString("he-IL")}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center" , fontWeight: 600, color: amountColor(netAmount, "#dc3545")}}>
                                                    {beautifyNumber(netAmount)}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, color: amountColor(vatAmount, "#28a745") }}>
                                                    {beautifyNumber(vatAmount)}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, color: amountColor(rowTotal, "inherit")}}>
                                                    {beautifyNumber(rowTotal)}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center" }}>
                                                    {doc.accountCaseId || "-"}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center" }}>
                                                    {doc.vat}%
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={doc.description || ''}>
                                                    {doc.description || '-'}
                                                </td>
                                            </tr>,
                                            ...(renderGroupChildren(doc, getExpenseDisplayVatAndNet, (d) => getExpenseDocType(d, pcnReportDetails), 'expense') || []),
                                        ];
                                    })}
                                    {/* Summary Row */}
                                    {(() => {
                                        const totalNet = sortedExpenses.reduce((sum, doc) => sum + getExpenseDisplayVatAndNet(doc).netAmount, 0);
                                        const totalVat = sortedExpenses.reduce((sum, doc) => sum + getExpenseDisplayVatAndNet(doc).vatAmount, 0);
                                        const totalAll = sortedExpenses.reduce((sum, doc) => sum + getExpenseDisplayVatAndNet(doc).totalAmount, 0);
                                        return (
                                    <tr style={{ 
                                        borderTop: "2px solid #dee2e6", 
                                        backgroundColor: "#f8f9fa",
                                        fontWeight: "bold"
                                    }}>
                                        <td colSpan="4" style={{ padding: "12px 8px", textAlign: "center" }}>
                                            {t('Total')}
                                        </td>
                                        <td style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, color: amountColor(totalNet, "#dc3545") }}>
                                            {beautifyNumber(totalNet)}
                                        </td>
                                        <td style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, color: amountColor(totalVat, "#28a745") }}>
                                            {beautifyNumber(totalVat)}
                                        </td>
                                        <td style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, color: amountColor(totalAll, "inherit") }}>
                                            {beautifyNumber(totalAll)}
                                        </td>
                                        <td colSpan="2" style={{ padding: "12px 8px", textAlign: "center" }}>
                                        </td>
                                        <td></td>
                                    </tr>
                                        );
                                    })()}
                                </tbody>
                            </table>
                        </div>
                        {/* pagination */}
                        <TablePagination
                          component="div"
                          count={sortedExpenses.length}
                          page={expensesPage}
                          onPageChange={(_, p) => setExpensesPage(p)}
                          rowsPerPage={expensesRowsPerPage}
                          onRowsPerPageChange={(e) => {
                            setExpensesRowsPerPage(parseInt(e.target.value, 10));
                            setExpensesPage(0);
                          }}
                          rowsPerPageOptions={[10, 25, 50, 100]}
                          labelRowsPerPage={t('Rows per page:')}
                          labelDisplayedRows={({ from, to, count }) =>
                            `${from}–${to} ${t('of')} ${count !== -1 ? count : `> ${to}`}`
                          }
                          sx={{ direction: 'ltr' }}
                        />
                    </div>
                )}

                {/* Section 3: EquipmentVAT (EquipmentTransactions) */}
                {(selectedFilter === 'all' || selectedFilter === 'EquipmentVAT') && hasEquipmentTransactions && (
                    <div style={{ marginBottom: '30px' }}>
                        <h3 style={{ 
                            textAlign: 'center', 
                            marginBottom: '15px', 
                            color: '#495057',
                            fontSize: '18px',
                            fontWeight: '600',
                            borderBottom: '2px solid #007bff',
                            paddingBottom: '8px'
                        }}>
                            {t("EquipmentVAT")}
                        </h3>
                        <div style={{ overflow: "auto" }}>
                            <table style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                fontSize: "14px",
                            }}>
                                <thead style={{ backgroundColor: "#e3f2fd" }}>
                                    <tr style={{ borderBottom: "2px solid rgb(37, 78, 118)" }}>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={equipmentSort.orderBy === 'entry'} direction={equipmentSort.orderBy === 'entry' ? equipmentSort.order : 'asc'} onClick={createSortHandler('equipment', 'entry')}>{t('Entry')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={equipmentSort.orderBy === 'type'} direction={equipmentSort.orderBy === 'type' ? equipmentSort.order : 'asc'} onClick={createSortHandler('equipment', 'type')}>{t('Type')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={equipmentSort.orderBy === 'reference'} direction={equipmentSort.orderBy === 'reference' ? equipmentSort.order : 'asc'} onClick={createSortHandler('equipment', 'reference')}>{t('Reference')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={equipmentSort.orderBy === 'documentDate'} direction={equipmentSort.orderBy === 'documentDate' ? equipmentSort.order : 'asc'} onClick={createSortHandler('equipment', 'documentDate')}>{t('DocumentDate')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={equipmentSort.orderBy === 'amountBeforeVat'} direction={equipmentSort.orderBy === 'amountBeforeVat' ? equipmentSort.order : 'asc'} onClick={createSortHandler('equipment', 'amountBeforeVat')}>{t('AmountBeforeVAT')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={equipmentSort.orderBy === 'vatAmount'} direction={equipmentSort.orderBy === 'vatAmount' ? equipmentSort.order : 'asc'} onClick={createSortHandler('equipment', 'vatAmount')}>{t('VATAmount')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={equipmentSort.orderBy === 'totalAmount'} direction={equipmentSort.orderBy === 'totalAmount' ? equipmentSort.order : 'asc'} onClick={createSortHandler('equipment', 'totalAmount')}>{t('TotalAmountIncludingVAT')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={equipmentSort.orderBy === 'companyId'} direction={equipmentSort.orderBy === 'companyId' ? equipmentSort.order : 'asc'} onClick={createSortHandler('equipment', 'companyId')}>{t('CompanyId')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={equipmentSort.orderBy === 'vatPercentage'} direction={equipmentSort.orderBy === 'vatPercentage' ? equipmentSort.order : 'asc'} onClick={createSortHandler('equipment', 'vatPercentage')}>{t('VATPercentage')}</TableSortLabel>
                                        </th>
                                        <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #dee2e6" }}>
                                            <TableSortLabel active={equipmentSort.orderBy === 'description'} direction={equipmentSort.orderBy === 'description' ? equipmentSort.order : 'asc'} onClick={createSortHandler('equipment', 'description')}>{t('Description')}</TableSortLabel>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedEquipment
                                        .slice(equipmentPage * equipmentRowsPerPage, equipmentPage * equipmentRowsPerPage + equipmentRowsPerPage)
                                        .flatMap((doc, index) => {
                                        const { vatAmount, netAmount, totalAmount: rowTotal } = getExpenseDisplayVatAndNet(doc);
                                        const totalAmount = sortedEquipment.reduce((sum, d) => sum + getExpenseDisplayVatAndNet(d).totalAmount, 0);
                                        const percentage = totalAmount > 0 ? ((rowTotal / totalAmount) * 100).toFixed(2) : '0.00';
                                        const rowKey = `equipment-${doc.id ?? doc.Id ?? index}-${index}`;
                                        return [
                                            <tr
                                                key={rowKey}
                                                style={{
                                                    borderBottom: "1px solid #dee2e6",
                                                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8f9fa",
                                                    transition: "background-color 0.2s ease",
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => handleRowClick(doc)}
                                                onMouseEnter={(e) => {
                                                    e.target.closest("tr").style.backgroundColor = "#e3f2fd";
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.closest("tr").style.backgroundColor =
                                                        index % 2 === 0 ? "#ffffff" : "#f8f9fa";
                                                }}
                                            >
                                                <td style={{ padding: "12px 8px", textAlign: "center" }}>
                                                    {`(${doc.uploadNumber || 'N/A'}) ${new Date(doc.documentDate).toLocaleDateString("he-IL", { month: '2-digit', year: '2-digit' })}`}
                                                    {doc._groupedCount > 1 && (
                                                        <Chip
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); toggleGroup(doc._mergeKey || doc._groupId); }}
                                                            label={`${t('Grouped', { defaultValue: 'Grouped' })} (${doc._groupedCount}) ${expandedGroups.has(doc._mergeKey || doc._groupId) ? '▲' : '▼'}`}
                                                            title={(doc._groupedUploads || []).join(', ')}
                                                            sx={{ ml: 0.5, height: 18, fontSize: 10, backgroundColor: '#ede7f6', color: '#5e35b1', cursor: 'pointer', '&:hover': { backgroundColor: '#d1c4e9' } }}
                                                        />
                                                    )}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center" }}>
                                                    {(doc.pcnCompositeType === 'C+M' ? 'C' : (pcnReportDetails.expenseType?.[doc.id] ?? pcnReportDetails.expenseType?.[doc.Id] ?? '-'))}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center" }}>
                                                    {doc.reference || '-'}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center" }}>
                                                    {new Date(doc.documentDate).toLocaleDateString("he-IL")}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, color: amountColor(netAmount, "#dc3545") }}>
                                                    {beautifyNumber(netAmount)}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, color: amountColor(vatAmount, "#28a745") }}>
                                                    {beautifyNumber(vatAmount)}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, color: amountColor(rowTotal, "inherit") }}>
                                                    {beautifyNumber(rowTotal)}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center" }}>
                                                    {doc.accountCaseId || "-"}
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center" }}>
                                                    {doc.vat}%
                                                </td>
                                                <td style={{ padding: "12px 8px", textAlign: "center", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={doc.description || ''}>
                                                    {doc.description || '-'}
                                                </td>
                                            </tr>,
                                            ...(renderGroupChildren(doc, getExpenseDisplayVatAndNet, (d) => getExpenseDocType(d, pcnReportDetails), 'equipment') || []),
                                        ];
                                    })}
                                    {/* Summary Row */}
                                    {(() => {
                                        const totalNet = sortedEquipment.reduce((sum, doc) => sum + getExpenseDisplayVatAndNet(doc).netAmount, 0);
                                        const totalVat = sortedEquipment.reduce((sum, doc) => sum + getExpenseDisplayVatAndNet(doc).vatAmount, 0);
                                        const totalAll = sortedEquipment.reduce((sum, doc) => sum + getExpenseDisplayVatAndNet(doc).totalAmount, 0);
                                        return (
                                    <tr style={{ 
                                        borderTop: "2px solid #dee2e6", 
                                        backgroundColor: "#f8f9fa",
                                        fontWeight: "bold"
                                    }}>
                                        <td colSpan="4" style={{ padding: "12px 8px", textAlign: "center" }}>
                                            {t('Total')}
                                        </td>
                                        <td style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, color: amountColor(totalNet, "#dc3545") }}>
                                            {beautifyNumber(totalNet)}
                                        </td>
                                        <td style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, color: amountColor(totalVat, "#28a745") }}>
                                            {beautifyNumber(totalVat)}
                                        </td>
                                        <td style={{ padding: "12px 8px", textAlign: "center", fontWeight: 600, color: amountColor(totalAll, "inherit") }}>
                                            {beautifyNumber(totalAll)}
                                        </td>
                                        <td colSpan="2" style={{ padding: "12px 8px", textAlign: "center" }}>
                                        </td>
                                        <td></td>
                                    </tr>
                                        );
                                    })()}
                                </tbody>
                            </table>
                        </div>
                        {/* pagination */}
                        <TablePagination
                          component="div"
                          count={sortedEquipment.length}
                          page={equipmentPage}
                          onPageChange={(_, p) => setEquipmentPage(p)}
                          rowsPerPage={equipmentRowsPerPage}
                          onRowsPerPageChange={(e) => {
                            setEquipmentRowsPerPage(parseInt(e.target.value, 10));
                            setEquipmentPage(0);
                          }}
                          rowsPerPageOptions={[10, 25, 50, 100]}
                          labelRowsPerPage={t('Rows per page:')}
                          labelDisplayedRows={({ from, to, count }) =>
                            `${from}–${to} ${t('of')} ${count !== -1 ? count : `> ${to}`}`
                          }
                          sx={{ direction: 'ltr' }}
                        />
                    </div>
                )}

                {/* Footer */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    gap: 2, 
                    mt: 3,
                    pt: 2,
                    borderTop: '1px solid #dee2e6'
                }}>
                    <Button 
                        variant="outlined" 
                        onClick={onClose}
                        sx={{
                            borderRadius: '8px',
                            borderColor: '#6c757d',
                            color: '#6c757d',
                            '&:hover': {
                                borderColor: '#495057',
                                backgroundColor: 'rgba(108, 117, 125, 0.04)'
                            }
                        }}
                    >
                        {t('Close')}
                    </Button>
                </Box>
            </Box>
        </Modal>
        </>
    );
};

export default memo(PCNReportDetailsModal); 